import { Suspense, lazy as reactLazy, type ReactNode } from 'react';

/**
 * Wave 314e — `lazy()` with auto-recovery on stale chunk imports.
 *
 * After a deploy, returning users still hold the OLD index.html in memory
 * which references chunk hashes that no longer exist on Pages. The import
 * throws "Failed to fetch dynamically imported module" and React Router
 * surfaces "Unexpected Application Error". Anderson hit this on every
 * tab swap right after a smoke deploy.
 *
 * Fix: catch the import failure, set a sessionStorage flag (so we never
 * loop on a real broken bundle), and force one reload. The reload picks
 * up the fresh index.html with current hashes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazy: typeof reactLazy = ((factory: () => Promise<any>) =>
  reactLazy(async () => {
    try {
      const mod = await factory();
      // Successful load on the new bundle — clear the flag so a
      // future deploy can trigger one fresh reload.
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('builder:chunk-reload-attempted');
      }
      return mod;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
          msg,
        );
      if (isChunkError && typeof window !== 'undefined') {
        const KEY = 'builder:chunk-reload-attempted';
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, '1');
          window.location.reload();
          // Never-resolving promise so React doesn't render an error
          // before the page is replaced.
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return new Promise(() => {}) as never;
        }
      }
      throw err;
    }
  })) as typeof reactLazy;
import * as React from 'react';
import { Navigate, Outlet, createBrowserRouter, useLocation, useNavigate } from 'react-router-dom';
// Wave 26 — bundle-size pass.
//
// Keep the hot path eager: Login/Register, Projects list, ProjectDetails
// shell. Every other page — including project sub-tabs — is lazy-loaded.
//
// Wave 62 bundle-size fix (2026-05-23): StageTrackerPage, DailyLogsPage
// and FinancePage were eager imports that pushed the index chunk past the
// 1024 KB / 250 KB gzip CI gate. They are project-tab sub-routes rendered
// only after a PM navigates INTO a project and selects a tab — not on
// initial app load. Making them lazy has zero impact on first-paint; React
// Router's Suspense boundary shows a skeleton for <200ms on the first tab
// switch, which is acceptable. The eager set is now just the login funnel
// and the ProjectDetails shell.
//
// Public share viewers (`/compartilhado/...`) are ALSO lazy — an
// authenticated PM should never have that code in their entry bundle.
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';
import { isSessionValid, useAuth } from './store/auth.store';
import { PageSkeleton } from './components/ui/PageSkeleton';
// Wave 353 · brand/home destination resolved from NAV_REGISTRY.
import { getBrandHomeForRole } from './lib/nav-registry';
// Wave A · Mobile resilience pass.
import { MobileResilient } from './components/ui/MobileResilient';
import { QualityLoadingState } from './components/quality/QualityLoadingState';
// Wave codex-r8-legacy-redirects · 2026-05-19 · banner on legacy bookmark hop.
import { RedirectWithToast } from './components/RedirectWithToast';

// ---------------------------------------------------------------------
// Lazy pages — cold path + share-link viewers + project sub-tabs.
// ---------------------------------------------------------------------
// Wave 62 (2026-05-23): StageTrackerPage, DailyLogsPage, FinancePage moved
// from eager to lazy to bring index chunk under the 1024 KB / 250 KB gate.
const StageTrackerPage = lazy(() => import('./pages/StageTrackerPage'));
// W123.10 · DailyLogsPage não é mais montado no router — vive dentro de
// ObraRdo (tab da Mesa Obra). /rdos virou redirect.
const FinancePage = lazy(() => import('./pages/FinancePage'));
const VisaoGeralPage = lazy(() => import('./pages/VisaoGeralPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
// Wave 379BN · #87 fatia A · TimeCanvas wrapper consolida as 3 visões
// de tempo em /projects/:id/timeline?view=feed|gantt|calendar.
const TimeCanvasPage = lazy(() => import('./pages/TimeCanvasPage'));
// Wave merge-photos-fotos · 2026-05-21 · `/projects/:id/photos` now
// banner-redirects to `../fotos`. PhotosPage is no longer mounted by the
// router, but the component is kept in the bundle so any future "Albums"
// sub-view inside ProjectPhotoGalleryPage can lift its AlbumRail logic
// without re-implementing it.
// Wave NNNNN · Project-level photo gallery (paginated, filterable).
// W123.10 · ProjectPhotoGalleryPage não é mais montado no router — vive
// dentro de ObraFotos (tab da Mesa Obra). /fotos virou redirect.
// FEATURE A3 · 2026-05-21 · /projects/:id/semana — 7-day calendar with stages,
// measurements, FVS and PO deliveries scheduled in the current week.
const SemanaPage = lazy(() => import('./pages/SemanaPage'));
const OwnerPortalPage = lazy(() => import('./pages/OwnerPortalPage'));
// FEATURE B2 · H3.2 · 2026-05-21 — `/owner/projects/:id` now resolves to
// OwnerProjectDetailPage (Netflix-style hero + timeline). OwnerProjectPage
// is left in the source tree for reference and any future feature work
// (PIX card, stage drilldown) — it's no longer mounted by the router.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _OwnerProjectPageLegacy = lazy(() => import('./pages/OwnerProjectPage'));
const OwnerProjectDetailPage = lazy(() => import('./pages/OwnerProjectDetailPage'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
// W148 · 2026-05-28 — Página pública para cliente confirmar entrega da obra.
const DeliverySignaturePublicPage = lazy(() => import('./pages/DeliverySignaturePublicPage'));
// W152 · 2026-05-29 — Portal de acesso do interessado aprovado na vitrine.
const BuyerAccessPage = lazy(() => import('./pages/BuyerAccessPage'));
// Wave WelcomeFlow · 2026-05-20 — first-time PM welcome page.
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
// W123.13 · 2026-05-26 — Playground sandbox + post-login choice.
const PostLoginChoicePage = lazy(() => import('./pages/PostLoginChoicePage'));
const PlaygroundPage = lazy(() => import('./pages/PlaygroundPage'));
// Final Push #5 · 2026-05-20 — public status page (no auth, /status).
const StatusPage = lazy(() => import('./pages/StatusPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
// Wave merge-activity-audit · 2026-05-21 · `AuditPage` was previously lazy-
// loaded here for `/projects/:id/audit`. That route now renders a
// `RedirectWithToast` to `../atividade?view=audit`, where the body is
// `<AuditTrailTab>` directly. The named `AuditPage` export still lives in
// `pages/DocsPage` (delegates to `<AuditTrailTab>`) for any future caller.
// W151 P2 · `PurchasesPage` lazy import removido — a página foi substituída
// por `ComprasPage` (Wave 379U) e o const nunca era montado em rota (apenas
// `PurchasesLegacyRedirect`). Arquivo da página segue no repo só pra tests de
// origem; housekeeping de remoção do arquivo é wave separada.
const ComprasPage = lazy(() => import('./pages/ComprasPage'));
const QuotationResponsePublicPage = lazy(() => import('./pages/QuotationResponsePublicPage'));
const LeilaoResponsePublicPage = lazy(() => import('./pages/LeilaoResponsePublicPage'));
// Wave 379AJ · public budget approval landing
const BudgetApprovalPublicPage = lazy(() => import('./pages/BudgetApprovalPublicPage'));
// Wave 379BM · public supplier portal (#94 slice d)
const SupplierPortalPublicPage = lazy(() => import('./pages/SupplierPortalPublicPage'));
// W149 · 2026-05-28 · compliance pages (LGPD + termos).
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ChangeOrdersPage = lazy(() => import('./pages/ChangeOrdersPage'));
const ApprovePage = lazy(() => import('./pages/ApprovePage'));
const IssuesPage = lazy(() => import('./pages/IssuesPage'));
// W123.4 · BudgetPage no longer mounted directly (orcamento → plano).
// Mantido como lazy import pra W123.5 (mesa Plano) reusar.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _BudgetPage = lazy(() => import('./pages/BudgetPage'));
// W123.6 · wizard de criação de obra (6 steps + resultado).
const WizardObraPage = lazy(() => import('./pages/WizardObraPage'));
// Wave 394 · Budget Excel import wizard.
const BudgetExcelImportPage = lazy(() => import('./pages/BudgetExcelImportPage'));
// Wave MMMM · Compras Excel import wizard.
const ComprasExcelImportPage = lazy(() => import('./pages/ComprasExcelImportPage'));
// Wave NNNN · Cronograma Excel import wizard.
const CronogramaExcelImportPage = lazy(() => import('./pages/CronogramaExcelImportPage'));
// Wave NNNN · Fornecedores Excel import wizard.
const FornecedoresExcelImportPage = lazy(() => import('./pages/FornecedoresExcelImportPage'));
// Wave OOOO · Medições Excel import wizard.
const MedicoesExcelImportPage = lazy(() => import('./pages/MedicoesExcelImportPage'));
// Wave DP2 · 5 new Excel importers.
const VerbaExcelImportPage = lazy(() => import('./pages/VerbaExcelImportPage'));
const EstoqueExcelImportPage = lazy(() => import('./pages/EstoqueExcelImportPage'));
const DailyLogsExcelImportPage = lazy(() => import('./pages/DailyLogsExcelImportPage'));
const PranchasExcelImportPage = lazy(() => import('./pages/PranchasExcelImportPage'));
const TrenaExcelImportPage = lazy(() => import('./pages/TrenaExcelImportPage'));
// Wave 79 — financial depth
const FinancialDepthPage = lazy(() => import('./pages/FinancialDepthPage').then((m) => ({ default: m.FinancialDepthPage })));
// Wave 80 — drawings + RFI
// W123.10 · DrawingsRfiPage não é mais montado no router — vive dentro de
// ObraPranchas (tab da Mesa Obra). /pranchas e /rfi viraram redirects.
// Wave 86 — anomaly inbox
const AnomaliasPage = lazy(() => import('./pages/AnomaliasPage'));
// Wave 81 — compliance & pós-obra
const CompliancePage = lazy(() => import('./pages/CompliancePage').then((m) => ({ default: m.CompliancePage })));
// Wave 82 → 379CP — SchedulePage legado (Gantt + RUP). Gantt foi
// portado pro TimeCanvas (slice (d) do #87) e a rota agora redireciona
// pra /timeline?view=gantt. RUP ainda vive lá; lazy import é mantido só
// pra não quebrar imports indiretos enquanto a fatia de RUP-as-canvas
// não chega. Não está plugado em nenhuma route do file: o redirect 308
// abaixo (`/cronograma-rup` → `?view=gantt`) substitui completamente.
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const SupplierDetailsPage = lazy(() => import('./pages/SupplierDetailsPage'));
// Wave 1.5 · 2026-05-10 · Catálogo de SKUs (vitrine + inline-add)
const CatalogoPage = lazy(() => import('./pages/CatalogoPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
// Wave ggggg · 2026-05-16 · Admin home page (tile rollup of all admin surfaces).
const AdminHomePage = lazy(() => import('./pages/AdminHomePage'));
// Wave comando · 2026-05-17 · Command Center single-pane-of-glass.
const ComandoPage = lazy(() => import('./pages/ComandoPage'));
// Wave comando · 2026-05-17 · Configurações consolidadas (users/backups/tenants/flags).
const AdminConfiguracoesPage = lazy(() => import('./pages/AdminConfiguracoesPage'));
// Wave comando · 2026-05-17 · Históricos consolidados (audit/deploys/atividade).
const AdminHistoricosPage = lazy(() => import('./pages/AdminHistoricosPage'));
const AdminCommandControlPage = lazy(() => import('./pages/AdminCommandControlPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminDeploysPage = lazy(() => import('./pages/AdminDeploysPage'));
const AdminSaudePage = lazy(() => import('./pages/AdminSaudePage'));
const AdminAutonomiaPage = lazy(() => import('./pages/AdminAutonomiaPage'));
// Wave 347 · Housy Brain command center.
const AdminBrainPage = lazy(() => import('./pages/AdminBrainPage'));
const AdminGoalPlansPage = lazy(() => import('./pages/AdminGoalPlansPage'));
const AdminOrchestratorPage = lazy(() => import('./pages/AdminOrchestratorPage'));
const AdminConectoresPage = lazy(() => import('./pages/AdminConectoresPage'));
// Wave 369 R4 · proposer A/B win/loss scoreboard.
const AdminProposerAbPage = lazy(() => import('./pages/AdminProposerAbPage'));
// Wave 285e R2 · Housy Unknowns Curation admin UI.
const AdminHousyUnknownsPage = lazy(() => import('./pages/AdminHousyUnknownsPage'));
// Wave 379CU · Housy debug QA harness (force-state isolated stage).
const AdminHousyDebugPage = lazy(() => import('./pages/AdminHousyDebugPage'));
// Wave 380 · Product Analytics (autonomy metrics + roadmap).
const AdminProductAnalyticsPage = lazy(() => import('./pages/AdminProductAnalyticsPage'));
const AdminDecisionGraphPage = lazy(() => import('./pages/AdminDecisionGraphPage'));
const BuyerPortalPage = lazy(() => import('./pages/BuyerPortalPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const CarteiraPage = lazy(() => import('./pages/CarteiraPage'));
const AlertasPage = lazy(() => import('./pages/AlertasPage'));
const ApprovalsInboxPage = lazy(() => import('./pages/ApprovalsInboxPage'));
// Wave 351-C — unified inbox (Hoje / Pendências / Decisões).
// Single page that consumes /v1/inbox and dispatches into the right
// surface (Prumo for proposals, /admin/saude for errors/heal failures).
const InboxPage = lazy(() => import('./pages/InboxPage'));
const AtividadePage = lazy(() => import('./pages/AtividadePage'));
// Wave 27 — supplier catalog + Curva ABC report.
const CurvaAbcPage = lazy(() => import('./pages/CurvaAbcPage'));
// Wave 28 — punch list de entrega (delivery kanban-lite).
const PunchListPage = lazy(() => import('./pages/PunchListPage'));
// W123.12 · 2026-05-26 · Closeout conduzido — substitui PunchListPage em /entrega.
const EntregaPage = lazy(() => import('./pages/mesa/EntregaPage'));
// Wave 29 — mão-de-obra diária (per-role labor tracking).
const LaborPage = lazy(() => import('./pages/LaborPage'));
// Wave 30 — RNC (Registro de Não Conformidade — quality register).
// W123.10 · RncPage não é mais montado no router — vive dentro de ObraRnc.
// Wave 31 — FVS (Ficha de Verificação de Serviço — preventive checklists).
// W123.10 · FvsPage não é mais montado no router — vive dentro de ObraFvs.
// Wave 32 — Relatório Executivo (CEO-level cross-project dashboard).
const ExecutivoPage = lazy(() => import('./pages/ExecutivoPage'));
// Wave 33 — user approval queue (admin + PM).
const ApproveUsersPage = lazy(() => import('./pages/ApproveUsersPage'));
// Wave 43 — Vitrine admin (house-sales showcase management).
const VitrineListPage = lazy(() => import('./pages/VitrineListPage'));
const VitrineDetailsPage = lazy(() => import('./pages/VitrineDetailsPage'));
// Module C / D / ObraFit — cost-control & operations sub-tabs.
const VerbaPage = lazy(() => import('./pages/VerbaPage'));
const DemonstrativoPage = lazy(() => import('./pages/DemonstrativoPage'));
const EstoquePage = lazy(() => import('./pages/EstoquePage'));
const ChecklistPage = lazy(() => import('./pages/ChecklistPage'));
// W151 P2 · `QuotationPage` lazy import removido — substituída por `ComprasPage`
// (Wave 379U), const nunca montado em rota. Arquivo segue no repo só pra tests.
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
// Wave relatorios-canonicos · 2026-05-21 · /projects/:id/relatorios · 3 relatórios canônicos (project-scoped).
const RelatoriosPage = lazy(() => import('./pages/RelatoriosPage'));
// Wave 35 — help center.
const AjudaPage = lazy(() => import('./pages/AjudaPage'));
// Wave UUUU — Qualidade Hub cross-project mode top-level page.
const QualidadeHubPage = lazy(() => import('./pages/QualidadeHubPage'));
// Wave 353 — field-first surfaces. Round 1 ships shells; round 2/3
// fill the 3-tap Campo flow and the grouped Hoje queue.
const CampoPage = lazy(() => import('./pages/CampoPage'));
const HojePage = lazy(() => import('./pages/HojePage'));
// FEATURE B1 · H2.2 · 2026-05-21 — Cross-obra "Hoje" hub for PM/admin.
// Routed alongside HojePage via `HojeRouter` below: PM/admin see the new
// cross-obra hub; foreman/worker fall through to the field-first queue.
const HojeCrossObraPage = lazy(() => import('./pages/HojeCrossObraPage'));
// Wave 384 — Monthly Close Page
const AdminMonthlyClosePage = lazy(() => import('./pages/AdminMonthlyClosePage'));
// Wave 390 — Financial Risk Autopilot admin page
const AdminFinancialRiskPage = lazy(() => import('./pages/AdminFinancialRiskPage'));
const AdminIncidentsPage = lazy(() => import('./pages/AdminIncidentsPage'));
// Wave 385 — Data Self-Heal admin page
const AdminDataSelfHealPage = lazy(() => import('./pages/AdminDataSelfHealPage'));
// Wave 386 — Workflow Self-Heal admin page
const AdminWorkflowSelfHealPage = lazy(() => import('./pages/AdminWorkflowSelfHealPage'));
// Wave 387 — Procurement Autopilot admin page
const AdminProcurementAutopilotPage = lazy(() => import('./pages/AdminProcurementAutopilotPage'));
// LR-W3 · 2026-05-14 · Admin Auctions list + detail (leilão reverso PM admin)
const AdminAuctionsPage = lazy(() => import('./pages/AdminAuctionsPage'));
const AdminAuctionDetailPage = lazy(() => import('./pages/AdminAuctionDetailPage'));
// LR-W4 · 2026-05-15 · Broker / Supplier-facing auction dashboard.
const BrokerDashboardPage = lazy(() => import('./pages/BrokerDashboardPage'));
const BrokerAuctionHistoryPage = lazy(() => import('./pages/BrokerAuctionHistoryPage'));
const BrokerRoundDetailPage = lazy(() => import('./pages/BrokerRoundDetailPage'));
// LR-W2 · #51 · Procurement Cockpit (Modality Recommender shadow lab · admin/PM)
const ProcurementCockpitPage = lazy(() => import('./pages/ProcurementCockpitPage'));
// LR-W2 · #51 · Procurement Frameworks (contratos guarda-chuva · promote + drawdown · admin/PM)
const ProcurementFrameworksPage = lazy(() => import('./pages/ProcurementFrameworksPage'));
// LR-W2 · #56 · Procurement Dashboard (savings cross-obras · KPIs · per-obra table · admin/PM)
const ProcurementDashboardPage = lazy(() => import('./pages/ProcurementDashboardPage'));
// Trena · Wave 1C · Orçamentador L2 (passo zero da obra) · 2026-05-11
const TrenaListPage = lazy(() => import('./pages/TrenaListPage'));
// Wave (project-trena-overview) · 2026-05-15 · Visão geral das estimativas por projeto.
const ProjectTrenaOverviewPage = lazy(() => import('./pages/ProjectTrenaOverviewPage'));
// W156 · 2026-06-01 — ObraHojePage desmontada; `/obra-hoje` agora
// banner-redirect pra `/hoje` (HojeObraPage canônica). Componente segue
// no bundle (re-uso por ObraCockpit/WeatherStrip) mas sem rota própria.
// Wave D · Project home cockpit — 3-block operating view (Hoje / Travado / 7 dias).
const ProjectHomePage = lazy(() => import('./pages/ProjectHomePage'));
// Wave FFFFF · Project-scoped activity feed.
const ProjectActivityPage = lazy(() => import('./pages/ProjectActivityPage'));
// Wave 393A · 2026-05-15 · Admin override audit history (admin_override_commit entries).
const AdminTrenaOverridesPage = lazy(() => import('./pages/AdminTrenaOverridesPage'));
// Wave QQQ · 2026-05-16 · Admin follow-ups telemetry (follow_ups_audit read surface).
const AdminFollowUpsPage = lazy(() => import('./pages/AdminFollowUpsPage'));
// Wave 392 · 2026-05-15 · Admin Trena R2 storage stats dashboard.
const AdminTrenaStoragePage = lazy(() => import('./pages/AdminTrenaStoragePage'));
// Wave 393B · 2026-05-15 · Admin Trena compositions compat audit.
const AdminTrenaCompositionsPage = lazy(() => import('./pages/AdminTrenaCompositionsPage'));
const TrenaEstimatePage = lazy(() => import('./pages/TrenaEstimatePage'));
// Wave MMMMM · 2026-05-16 · Side-by-side comparison of two trena estimates.
// Lazy so it doesn't bloat first paint (constraint: bundle).
const TrenaEstimateComparePage = lazy(() => import('./pages/TrenaEstimateComparePage'));
// WAVE 34 · H3.4 · 2026-05-22 · Confidence multi-estimate compare (2..3
// scenarios; central + band + buckets + sistemas + auto-analysis).
const TrenaCompareEstimatesPage = lazy(() => import('./pages/TrenaCompareEstimatesPage'));
// Trena · Picker · 2026-05-12 · entrada via sidebar /trena · pergunta qual obra
const TrenaPickerPage = lazy(() => import('./pages/TrenaPickerPage'));
// Dinheiro · Picker · P1 fix · 2026-05-27 · entrada via sidebar Financeiro /dinheiro
const DinheiroPickerPage = lazy(() => import('./pages/DinheiroPickerPage'));
// W149 · 2026-05-28 · cross-obra views para buyer + controller.
const ComprasGeralPage = lazy(() => import('./pages/ComprasGeralPage'));
const DinheiroGeralPage = lazy(() => import('./pages/DinheiroGeralPage'));
// Trena · Wizard · W1F · 2026-05-12 · fluxo for-dummies: nome → doc → revisar → calcular → aprovar
const TrenaWizardPage = lazy(() => import('./pages/TrenaWizardPage'));
// Trena · Done Page · 2026-05-15 · post-commit confirmation (bookmarkable)
const TrenaDonePage = lazy(() => import('./pages/TrenaDonePage'));
// FEATURE C1 · 2026-05-21 · quick-photo confirm page (foreman bypass do wizard)
const TrenaQuickPhotoConfirmPage = lazy(() => import('./pages/TrenaQuickPhotoConfirmPage'));
// Admin · Insumos · W1I · 2026-05-12 · catálogo de preços (SINAPI + ajustes via Prumo)
const AdminInsumosPage = lazy(() => import('./pages/AdminInsumosPage'));
// Wave 388 — Resource Optimizer admin page
const AdminResourceOptimizerPage = lazy(() => import('./pages/AdminResourceOptimizerPage'));
// Wave 389 — Rule Autopilot admin page
const AdminRuleAutopilotPage = lazy(() => import('./pages/AdminRuleAutopilotPage'));
// Wave 381 — PM Loop admin page
const AdminPMLoopPage = lazy(() => import('./pages/AdminPMLoopPage'));
// Wave 391 — Quality Autopilot admin page
const AdminQualityAutopilotPage = lazy(() => import('./pages/AdminQualityAutopilotPage'));
// Wave 392 — Claims Detection admin page
const AdminClaimsDetectionPage = lazy(() => import('./pages/AdminClaimsDetectionPage'));
// Wave 393 — Owner Trust Plus admin page
const AdminOwnerTrustPlusPage = lazy(() => import('./pages/AdminOwnerTrustPlusPage'));
// Wave 394 — Agent Governor admin page
const AdminAgentGovernorPage = lazy(() => import('./pages/AdminAgentGovernorPage'));
// Wave 395 — Self-Tuning admin page
const AdminSelfTuningPage = lazy(() => import('./pages/AdminSelfTuningPage'));
// Wave 396 — Daily Brief admin page
const AdminDailyBriefPage = lazy(() => import('./pages/AdminDailyBriefPage'));
// Wave 403 — SLA admin page
const AdminSLAPage = lazy(() => import('./pages/AdminSLAPage'));
// Wave 410-415 · Tier F wave foundations
const AdminTenantsPage = lazy(() => import('./pages/AdminTenantsPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const AdminBulkOpsPage = lazy(() => import('./pages/AdminBulkOpsPage'));
const AdminAuditLogPage = lazy(() => import('./pages/AdminAuditLogPage'));
// Error tracking · 2026-05-20 — Cloudflare-native observability dashboard
const AdminObservabilityPage = lazy(() => import('./pages/AdminObservabilityPage'));
// 2026-05-21 — Trena funnel telemetry dashboard.
const AdminTrenaFunnelPage = lazy(() => import('./pages/AdminTrenaFunnelPage'));
const SettingsNotificationsPage = lazy(() => import('./pages/SettingsNotificationsPage'));
// Wave pm-uom · 2026-05-17 · Unidades de medida customizadas do tenant.
const SettingsUomPage = lazy(() => import('./pages/SettingsUomPage'));
const AdminBackupsPage = lazy(() => import('./pages/AdminBackupsPage'));
// Wave 383 — Radar Critic admin page
const AdminRadarCriticPage = lazy(() => import('./pages/AdminRadarCriticPage'));
// Wave 368 — Chaos admin page
const AdminChaosPage = lazy(() => import('./pages/AdminChaosPage'));
// Wave 41 — Meta WhatsApp template watcher admin page
const AdminMetaTemplatesPage = lazy(() => import('./pages/AdminMetaTemplatesPage'));
// Wave 47 · 2026-05-23 · Generative Engineering Labs page (AI sketch demo).
const AdminLabsGenerativePage = lazy(() => import('./pages/admin/AdminLabsGenerativePage'));
// Wave 67 F2.4 · 2026-05-23 · Generative Lab history (admin-only).
const AdminLabsGenerativeHistoryPage = lazy(() => import('./pages/admin/AdminLabsGenerativeHistoryPage'));
// Wave SYSTEM-TASKS · 2026-05-20 — Smart scheduler com self-healing.
const AdminSystemTasksPage = lazy(() => import('./pages/AdminSystemTasksPage'));
// Wave 397 — Exceptions Only admin page
const AdminExceptionsOnlyPage = lazy(() => import('./pages/AdminExceptionsOnlyPage'));
// Wave 400 — Audit Pack admin page
const AdminAuditPackPage = lazy(() => import('./pages/AdminAuditPackPage'));
// Wave 398 — Memory Graph admin page
const AdminMemoryGraphPage = lazy(() => import('./pages/AdminMemoryGraphPage'));
// Wave 399 — Simulation admin page
const AdminSimulationPage = lazy(() => import('./pages/AdminSimulationPage'));
// Wave 405 — Benchmarking admin page
const AdminBenchmarkingPage = lazy(() => import('./pages/AdminBenchmarkingPage'));
// Wave 404 — SOP admin page
const AdminSOPPage = lazy(() => import('./pages/AdminSOPPage'));
// Wave 402 — Policy admin page
const AdminPolicyPage = lazy(() => import('./pages/AdminPolicyPage'));
// Wave M — Trena Sanity Ranges admin page
const AdminTrenaSanityRangesPage = lazy(() => import('./pages/AdminTrenaSanityRangesPage'));
// Wave 25 — public share viewers.
const SharedMedicaoPage = lazy(() => import('./pages/SharedMedicaoPage'));
const SharedDrePage = lazy(() => import('./pages/SharedDrePage'));
// W123.1 PR2 · 2026-05-25 — site-fechado page. Render puro, sem fetch.
// Mounted at /maintenance; `MaintenanceRedirect` envia toda rota PM-only
// pra cá quando a flag global `__BUILDER_MAINTENANCE__` está ligada.
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));

// W123.4 · 2026-05-26 · Placeholders das 4 novas mesas de trabalho.
// Substituídos por mesas reais em W123.5/6/7/9. Render puro (sem fetch).
// W123.7 · 2026-05-26 · Plano agora é a mesa real (7 tabs via ?tab=).
const MesaTrabalhoPlanoPage = lazy(() => import('./pages/mesa/MesaTrabalhoPlanoPage'));
// W123.10 · 2026-05-26 · Mesa Obra real (substitui placeholder W123.4).
// Antes: ./pages/placeholders/MesaTrabalhoObraPage (EmptyState shell).
// Agora: ./pages/mesa/MesaTrabalhoObraPage (5 tabs reais — RDO, Fotos,
// Pranchas e dúvidas, FVS, RNC). Espelho estrutural de Plano (W123.7).
const MesaTrabalhoObraPage = lazy(() => import('./pages/mesa/MesaTrabalhoObraPage'));
// W123.9 · 2026-05-26 · Mesa real (planejado/comprometido/pago/a-pagar/estoque)
const MesaTrabalhoDinheiroPage = lazy(() => import('./pages/mesa/MesaTrabalhoDinheiroPage'));
// W123.8 · 2026-05-26 · Mesa Compras = ComprasPage + filtros de visão.
const MesaTrabalhoComprasPage = lazy(() => import('./pages/mesa/MesaTrabalhoComprasPage'));
// W123.5 · /projects/:id/hoje agora aponta pra HojeObraPage real (não
// mais placeholder). A página consome /v1/projects/:id/next-action e
// renderiza ProximaAcaoCard único + resumo. Cravado em JP-1.
const HojeObraPage = lazy(() => import('./pages/HojeObraPage'));
// W146 · 2026-05-27 · QR code de presença (canteiro → WhatsApp).
const ProjectQrCodePage = lazy(() => import('./pages/ProjectQrCodePage'));

function LazyPortal({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <Suspense fallback={fallback ?? <PageSkeleton itemCount={5} />}>
      {children}
    </Suspense>
  );
}

/**
 * Wrap one lazy element at the leaf so a tab swap only shows the spinner
 * inside the project shell (not a full blank page).
 */
function Lazy({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <LazyPortal fallback={fallback}>{children}</LazyPortal>;
}

function RequireAuth() {
  const location = useLocation();
  const valid = useAuth((s) => isSessionValid(s));
  // Wave smoke-36 · 2026-05-20 — auto-hydrate user when token is valid
  // but the persisted `user` is null. This happens when a stored token
  // survives a reload but the user object was cleared, OR when a test
  // harness injects only `{ token, expiresAt }`. Without this hook the
  // SPA would render forever as "logged-in but no user", which made
  // SettingsPage flash "Entrar no Builder" (17-char fallback) and any
  // page that gates on `user.role` silently degrade. The /v1/auth/me
  // call is cheap (single row read) and only fires once per session.
  useHydrateUserOnce(valid);
  if (!valid) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

/**
 * Wave smoke-36 · 2026-05-20 — fire-and-forget /v1/auth/me hydration.
 *
 * Called from `RequireAuth`. Idempotent — uses a module-level latch so a
 * remount doesn't re-fetch. On failure (401, network, expired), the
 * latch resets so the next mount retries. The store update is silent;
 * any subscriber that depends on `user.role` re-renders automatically.
 */
let userHydrationInFlight = false;
function useHydrateUserOnce(authValid: boolean): void {
  React.useEffect(() => {
    if (!authValid) return;
    const state = useAuth.getState();
    if (state.user) return;
    if (userHydrationInFlight) return;
    userHydrationInFlight = true;
    // Lazy-load the api module so the chunk graph stays the same as
    // before this change (router.tsx already imports nothing from
    // api/client to keep the boot chunk lean).
    void import('./api/client')
      .then(({ api }) => api.me())
      .then((user) => {
        if (user) useAuth.setState({ user });
      })
      .catch(() => {
        // 401 / network / parse — leave user null. `request()` in the
        // api client handles 401 by bouncing to /login on its own.
      })
      .finally(() => {
        userHydrationInFlight = false;
      });
  }, [authValid]);
}

/**
 * W123.1 PR2 · 2026-05-25 · Maintenance redirect wrapper.
 *
 * O backend (PR1) já manda 503 + header `X-Maintenance: 1` em qualquer
 * path fora da whitelist. O client.ts captura o header, seta a flag
 * global `window.__BUILDER_MAINTENANCE__` e dispara o evento
 * `builder:maintenance`. Esse wrapper lê a flag em tempo de render E
 * escuta o evento (pra reagir quando o header chega DEPOIS do mount).
 *
 * Quando a flag está ligada E a rota atual não está na allowlist
 * abaixo, redireciona pra `/maintenance` (replace, sem entrada no
 * history). Caso contrário renderiza children/Outlet normalmente.
 *
 * A allowlist espelha o que o backend deixa passar:
 *   - /vitrine e /vitrine/:id          (PM showcase)
 *   - /o/:token e /budget-approval/... (owner approval links)
 *   - /owner e /owner/projects/:id     (Owner Portal read-only)
 *   - /admin/*                         (JP admin)
 *   - /login                           (re-auth)
 *   - /maintenance                     (a própria página)
 */
const MAINTENANCE_ALLOWLIST_EXACT = new Set([
  '/login',
  '/maintenance',
  '/owner',
]);
const MAINTENANCE_ALLOWLIST_PREFIX = [
  '/vitrine',
  '/o/',
  '/budget-approval/',
  '/owner/projects/',
  '/admin',
  '/ver/', // W152 · 2026-05-29 — acesso do interessado aprovado
];

function isMaintenanceAllowed(pathname: string): boolean {
  if (MAINTENANCE_ALLOWLIST_EXACT.has(pathname)) return true;
  for (const prefix of MAINTENANCE_ALLOWLIST_PREFIX) {
    if (pathname === prefix || pathname.startsWith(prefix + '/') || pathname.startsWith(prefix + '?')) {
      return true;
    }
    if (prefix.endsWith('/') && pathname.startsWith(prefix)) return true;
  }
  return false;
}

function readMaintenanceFlag(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window as unknown as { __BUILDER_MAINTENANCE__?: boolean })
    .__BUILDER_MAINTENANCE__;
}

function useMaintenanceFlag(): boolean {
  const [flag, setFlag] = React.useState<boolean>(() => readMaintenanceFlag());
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    function onEvent(): void {
      setFlag(readMaintenanceFlag());
    }
    window.addEventListener('builder:maintenance', onEvent);
    // Re-check on mount in case the header landed between the initial
    // useState read and the listener attach.
    setFlag(readMaintenanceFlag());
    return () => window.removeEventListener('builder:maintenance', onEvent);
  }, []);
  return flag;
}

function MaintenanceRedirect() {
  const location = useLocation();
  const inMaintenance = useMaintenanceFlag();
  if (inMaintenance && !isMaintenanceAllowed(location.pathname)) {
    return <Navigate to="/maintenance" replace />;
  }
  return <Outlet />;
}

/**
 * Banner-then-redirect para acesso negado por papel. Mesmo padrão de
 * `ReadOnlyRedirectBanner` / `CrewProjectRedirectBanner` em
 * `lib/route-guards.tsx`: mostra um aviso contextual por 1.5s antes de
 * navegar, pra o usuário entender por que a URL mudou debaixo dele
 * (padrão 32: redirect de acesso negado nunca silencioso).
 */
function AccessDeniedRedirect({ to, message }: { to: string; message: string }) {
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(false);
      navigate(to, { replace: true });
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [navigate, to]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
    >
      <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-md flex items-start gap-3">
        <div className="text-sm text-ink flex-1">
          <strong className="font-medium">Acesso restrito.</strong>{' '}
          <span className="text-ink-muted">{message}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Wave 332 · admin-only route guard. Redirects non-admins to /projects.
 * JP é o único role=admin no Builder; PMs e outros roles batem aqui e
 * voltam pra lista de obras, com aviso contextual (não mais silencioso —
 * padrão 32). Backend ainda valida via requireRole('admin') no endpoint,
 * isso é só pra UX.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <AccessDeniedRedirect
        to="/projects"
        message="Essa área é só para administradores. Voltando pra lista de obras."
      />
    );
  }
  return <>{children}</>;
}

/**
 * Wave 355 R1 + R1B + Run D · admin/PM management surfaces guard.
 *
 * Aplica-se a TODAS as rotas admin/PM-shaped que existem hoje:
 *   - `/admin/usuarios` (Wave 355 R1) — PM administra
 *     foreman/worker/client/broker; nunca admin nem outro PM.
 *   - `/vitrine` e `/vitrine/:id` (Wave 355 Run D) — superfícies de
 *     gestão de showcases; backend já gateia em
 *     `apps/api/src/routes/wave40.ts`
 *     (`showcaseAdminRoutes.use('*', requireRole('admin',
 *     'project_manager'))`). Antes do Run D essas rotas existiam como
 *     lazy bare e a página montava, fazia fetch e só então o backend
 *     respondia 403 — UX ruim e potencialmente vazaria chrome admin
 *     antes da resposta.
 *
 * Backend de referência:
 *   - `apps/api/src/routes/admin.ts` — admin sub-app com
 *     `requireRole('admin', 'project_manager')`.
 *   - `apps/api/src/services/admin-users.service.ts` — regra de
 *     produto pra `/admin/usuarios`.
 *   - `apps/api/src/routes/wave40.ts` — `showcaseAdminRoutes` com
 *     `requireRole('admin', 'project_manager')`.
 *
 * Comportamento (não muda entre superfícies):
 *   - logged-out → /login
 *   - role !== 'admin' && role !== 'project_manager' → /projects
 *     (que cai em RootRedirect e leva o read-only viewer pra /owner
 *     via getBrandHomeForRole).
 *   - admin / project_manager → renderiza children.
 */
function RequireAdminOrProjectManager({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'project_manager') {
    return (
      <AccessDeniedRedirect
        to="/projects"
        message="Essa área é só para gestores. Voltando pra lista de obras."
      />
    );
  }
  return <>{children}</>;
}

/**
 * LR-W4 · 2026-05-15 · Supplier-facing broker route guard.
 *
 * Any authenticated user may reach /broker routes. The backend
 * resolves user.email → suppliers.email and returns 403 if there is no
 * matching supplier record — in which case the page renders an empty
 * state with an instructional message. The guard here only ensures the
 * user is logged in; fine-grained supplier resolution is server-side.
 */
function RequireSupplier({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// 2026-05-11 (JP) · /projects/X/purchases (legado Wave 379U) redireciona
// pra /compras preservando hash (#po-Y) e querystring. Usa useNavigate
// porque <Navigate> descarta location.hash. JP reportou notif "Abrir PO"
// caindo no Kanban sem scrollar.
// Wave codex-r8-legacy-redirects · 2026-05-19 · agora renderiza o banner
// `RedirectWithToast` enquanto preserva o hash. O componente já dispara
// navigate com um pequeno delay; aqui pré-computamos o `to` preservando
// search+hash e delegamos o navigate pra ele — fonte única de banner.
function PurchasesLegacyRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/\/purchases$/, '/compras') + location.search + location.hash;
  return (
    <RedirectWithToast
      to={target}
      fromLabel="/purchases"
      toLabel="Compras"
    />
  );
}

function RootRedirect() {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  // Wave 353 · registry-driven. `getBrandHomeForRole` reads
  // `NAV_REGISTRY` so when `obras.to` flips (e.g. `/projects` →
  // `/campo` in this wave, or anywhere else later), the home
  // redirect follows for free. No hardcoded route literal.
  // Read-only roles resolve to `/owner` via the same helper.
  return <Navigate to={getBrandHomeForRole(user.role)} replace />;
}

/**
 * Wave 89 polish · /suppliers/* → /fornecedores/* preservando o sufixo.
 *
 * SPA não emite 301 real, mas Navigate replace=true substitui o entry no
 * history (back não volta pro /suppliers). useLocation.pathname devolve
 * `/suppliers/<sufixo>` — extraímos o sufixo e reconstruímos a URL pt-BR
 * com query/hash preservados.
 */
function RedirectSuppliersSubpath() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/suppliers/, '');
  const target = `/fornecedores${suffix}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

/**
 * FEATURE B1 · H2.2 · 2026-05-21 — `/hoje` dispatcher.
 *
 * admin / project_manager → cross-obra hub (HojeCrossObraPage).
 * Everyone else (foreman, worker)   → field-first queue (HojePage).
 *
 * Read-only viewers (client/broker) are already redirected to /owner
 * by both pages' useReadOnlyRedirect — they shouldn't reach here, but
 * if they do, HojePage's own guard catches them.
 */
function HojeRouter() {
  const role = useAuth((s) => s.user?.role);
  if (role === 'admin' || role === 'project_manager') {
    return (
      <Lazy>
        <HojeCrossObraPage />
      </Lazy>
    );
  }
  return (
    <Lazy>
      <HojePage />
    </Lazy>
  );
}

function RouterErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center space-y-4">
        <p className="text-ink font-medium">Algo deu errado nesta página.</p>
        <p className="text-ink-muted text-sm">
          Tente recarregar. Se o problema persistir, entre em contato com o suporte.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="min-h-[44px] px-2 text-sm text-brand underline"
        >
          Recarregar
        </button>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    errorElement: <RouterErrorFallback />,
    children: [
  { path: '/', element: <RootRedirect /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  // W123.1 PR2 · 2026-05-25 · Maintenance landing.
  // Render puro (zero fetch). Sem auth — precisa funcionar mesmo com
  // backend fechado (PR1 não bloqueia `/maintenance`).
  {
    path: '/maintenance',
    element: (
      <Lazy>
        <MaintenancePage />
      </Lazy>
    ),
  },
  // Wave 379V · resposta pública de cotação. Fornecedor abre o link
  // recebido por email; sem auth.
  {
    path: '/cotacao-resposta/:token',
    element: (
      <Lazy>
        <QuotationResponsePublicPage />
      </Lazy>
    ),
  },
  // LR-W1 · 2026-05-11 · portal público de lance em leilão reverso.
  // Fornecedor abre o link, vê itens, baixa o preço. Turnstile no submit.
  {
    path: '/leilao/:token',
    element: (
      <Lazy>
        <LeilaoResponsePublicPage />
      </Lazy>
    ),
  },
  // Wave 379AJ · aprovação pública do orçamento. Owner abre o link, vê
  // resumo e clica aprovar/rejeitar. Sem auth.
  {
    path: '/budget-approval/:token',
    element: (
      <Lazy>
        <BudgetApprovalPublicPage />
      </Lazy>
    ),
  },
  // Wave 379BM · supplier portal público (#94 slice d). Fornecedor abre
  // o link long-lived e vê dashboard read-only com cotações abertas, POs
  // ativas e pagamentos recentes. Sem auth — token é o secret.
  {
    path: '/supplier/:token',
    element: (
      <Lazy>
        <SupplierPortalPublicPage />
      </Lazy>
    ),
  },
  {
    path: '/accept-invitation',
    element: (
      <Lazy>
        <AcceptInvitationPage />
      </Lazy>
    ),
  },
  // W148 · 2026-05-28 · Cliente confirma entrega da obra via link público.
  {
    path: '/termo-entrega/:token',
    element: (
      <Lazy>
        <DeliverySignaturePublicPage />
      </Lazy>
    ),
  },
  // W152 · 2026-05-29 · Interessado aprovado acessa a obra via link público.
  // O token na URL É a credencial — sem login, a API valida tier/validade.
  {
    path: '/ver/:token',
    element: (
      <Lazy>
        <BuyerAccessPage />
      </Lazy>
    ),
  },
  // W149 · 2026-05-28 · LGPD compliance pages — public, no auth.
  {
    path: '/termos-de-uso',
    element: (
      <Lazy>
        <TermsOfUsePage />
      </Lazy>
    ),
  },
  {
    path: '/politica-de-privacidade',
    element: (
      <Lazy>
        <PrivacyPolicyPage />
      </Lazy>
    ),
  },
  // Wave 13.2: password reset — pt-BR slugs keep the URL readable.
  {
    path: '/esqueci-senha',
    element: (
      <Lazy>
        <ForgotPasswordPage />
      </Lazy>
    ),
  },
  {
    path: '/redefinir-senha',
    element: (
      <Lazy>
        <ResetPasswordPage />
      </Lazy>
    ),
  },
  // Wave 4: public one-shot approval link. Both `:token` path param and
  // `?token=` query string are supported so WhatsApp templates can use
  // either shape.
  {
    path: '/approve/:token',
    element: (
      <Lazy>
        <ApprovePage />
      </Lazy>
    ),
  },
  {
    path: '/approve',
    element: (
      <Lazy>
        <ApprovePage />
      </Lazy>
    ),
  },
  // Wave 10: scoped portal for external buyers and owners. The opaque
  // token in the URL IS the auth — no login, the API enforces role/scope.
  {
    path: '/c/:token',
    element: (
      <Lazy>
        <BuyerPortalPage role="buyer" />
      </Lazy>
    ),
  },
  {
    path: '/o/:token',
    element: (
      <Lazy>
        <BuyerPortalPage role="owner" />
      </Lazy>
    ),
  },
  // Wave 25: public, read-only share links for Medição and DRE.
  {
    path: '/compartilhado/medicao/:token',
    element: (
      <Lazy>
        <SharedMedicaoPage />
      </Lazy>
    ),
  },
  {
    path: '/compartilhado/dre/:token',
    element: (
      <Lazy>
        <SharedDrePage />
      </Lazy>
    ),
  },
  // Final Push #5 · 2026-05-20 — public status page.
  // SEM RequireAuth (precisa funcionar mesmo se a auth quebrar).
  {
    path: '/status',
    element: (
      <Lazy>
        <StatusPage />
      </Lazy>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      // W123.1 PR2 · 2026-05-25 — second layer: redirect to /maintenance
      // when the backend signals X-Maintenance: 1, unless the current
      // pathname is in MAINTENANCE_ALLOWLIST (owner portal, admin,
      // login). PM-facing routes (projects, hoje, vitrine etc.) all
      // bounce; rotas públicas (/o/:token, /budget-approval, etc.)
      // ficam fora dessa árvore — vivem antes do RequireAuth.
      {
        element: <MaintenanceRedirect />,
        children: [
      // Wave WelcomeFlow · 2026-05-20 — first-time PM landing.
      // RequireAuth gates it (need to know who you are); a server-side
      // signal from `auth.user.welcomedAt` + `projects.length === 0`
      // chooses /welcome vs /projects at app boot inside route-guards.
      {
        path: '/welcome',
        element: (
          <MobileResilient>
            <Lazy>
              <WelcomePage />
            </Lazy>
          </MobileResilient>
        ),
      },
      // W123.13 · escolha pós-login (primeira vez) entre operação real
      // vs playground. LoginPage roteia pra cá quando welcomedAt=null.
      {
        path: '/post-login',
        element: (
          <MobileResilient>
            <Lazy>
              <PostLoginChoicePage />
            </Lazy>
          </MobileResilient>
        ),
      },
      // W123.13 · Playground sandbox (tutorial vivo, side effects
      // bloqueados pelo middleware backend). PM-acessível.
      {
        path: '/playground',
        element: (
          <MobileResilient>
            <Lazy>
              <PlaygroundPage />
            </Lazy>
          </MobileResilient>
        ),
      },
      {
        path: '/projects',
        element: (
          <MobileResilient>
            <ProjectsPage />
          </MobileResilient>
        ),
      },
      // Wave 353 · field-first launcher. Crew (admin/PM/foreman/worker)
      // hit this from the brand link, BottomTabs Campo tab (round 2),
      // or `/`. Read-only viewers bounce to `/owner` via the
      // `useReadOnlyRedirect` page-level guard inside CampoPage.
      {
        path: '/campo',
        element: (
          <MobileResilient>
            <CampoPage />
          </MobileResilient>
        ),
      },
      // Trena · entrada via sidebar · pergunta qual obra (ou cria nova)
      // antes de mandar pra /projects/:id/trena. 2026-05-12.
      {
        path: '/trena',
        element: (
          <MobileResilient>
            <TrenaPickerPage />
          </MobileResilient>
        ),
      },
      // Dinheiro · entrada via sidebar Financeiro · pergunta qual obra
      // antes de mandar pra /projects/:id/dinheiro. P1 fix · 2026-05-27.
      {
        path: '/dinheiro',
        element: (
          <MobileResilient>
            <DinheiroPickerPage />
          </MobileResilient>
        ),
      },
      // W149 · 2026-05-28 · cross-obra: compras e dinheiro consolidados.
      {
        path: '/compras-geral',
        element: (
          <MobileResilient>
            <Lazy>
              <ComprasGeralPage />
            </Lazy>
          </MobileResilient>
        ),
      },
      {
        path: '/dinheiro-geral',
        element: (
          <MobileResilient>
            <Lazy>
              <DinheiroGeralPage />
            </Lazy>
          </MobileResilient>
        ),
      },
      // W123.6 · wizard de criação. DEVE vir antes de /projects/:id
      // pra "novo" não cair no detail. PM/admin-only via
      // RequireAdminOrProjectManager (espelha requireRole no backend).
      {
        path: '/projects/novo',
        element: (
          <RequireAdminOrProjectManager>
            <MobileResilient>
              <Lazy>
                <WizardObraPage />
              </Lazy>
            </MobileResilient>
          </RequireAdminOrProjectManager>
        ),
      },
      {
        path: '/projects/:id',
        element: (
          <MobileResilient>
            <ProjectDetailsPage />
          </MobileResilient>
        ),
        children: [
          // W125.2 · 2026-05-26 — landing /projects/:id → mesa /hoje.
          //
          // Histórico:
          //   • Wave D · cockpit ObraCockpit virou o index (substituiu a
          //     antiga VisaoGeralPage como landing).
          //   • W123.x · mesas novas (/hoje · /plano · /compras · /obra ·
          //     /dinheiro · /entrega) ficaram canônicas.
          //   • W125.2 · com o ProjectTabBar deletado (BUG B-3) e o sidebar
          //     + TopBar dinâmico (W124.4) entregando navegação completa,
          //     o landing virou redundante. Decisão JP: redirecionar pra
          //     /hoje (mesa próxima ação, única próxima decisão visível).
          //
          // ProjectHomePage continua disponível pelo /visao-geral pra quem
          // quiser o cockpit clássico (acessível pelo botão "Tudo" do
          // antigo strip também — mas o strip não renderiza mais).
          {
            index: true,
            element: <Navigate to="hoje" replace />,
          },
          {
            // Wave D · cockpit ObraCockpit como rota explícita pra rollback
            // (W125.2 movou pra cá; o index agora redireciona pra /hoje).
            path: 'cockpit',
            element: (
              <Lazy>
                <ProjectHomePage />
              </Lazy>
            ),
          },
          // ─── W123.4 · 2026-05-26 · novas mesas de trabalho (PLACEHOLDERS) ─
          // Estas rotas são alvo dos redirects das URLs legadas (ver mais
          // abaixo). Cada placeholder renderiza um EmptyState + link de
          // volta. W123.5/6/7/9 substituem cada componente pela mesa real.
          {
            path: 'hoje',
            element: (
              <Lazy>
                <HojeObraPage />
              </Lazy>
            ),
          },
          {
            path: 'plano',
            element: (
              <Lazy>
                <MesaTrabalhoPlanoPage />
              </Lazy>
            ),
          },
          {
            path: 'obra',
            element: (
              <Lazy>
                <MesaTrabalhoObraPage />
              </Lazy>
            ),
          },
          {
            path: 'dinheiro',
            element: (
              <Lazy>
                <MesaTrabalhoDinheiroPage />
              </Lazy>
            ),
          },
          { path: 'etapas', element: <Lazy><StageTrackerPage /></Lazy> },
          {
            // FEATURE A3 · /projects/:id/semana — calendário 7-dias
            // (stages, medições, FVS, POs) com navegação ← → de semana.
            path: 'semana',
            element: (
              <Lazy>
                <SemanaPage />
              </Lazy>
            ),
          },
          {
            // W156 simplificação · 2026-06-01 — fim da fragmentação (padrão 14).
            //
            // `/projects/:id/hoje` (HojeObraPage) e `/projects/:id/obra-hoje`
            // (ObraHojePage) eram duas rotas paralelas pro mesmo conceito
            // ("o que fazer hoje nesta obra"). A nav (PM_NAV/CREW_NAV) e a
            // SideNav já apontavam só pra `/hoje` — `/obra-hoje` só sobrevivia
            // no catálogo "TUDO" e em links "Ver tudo/inbox" internos.
            //
            // Decisão JP: `/hoje` é a canônica. `/obra-hoje` banner-redirect
            // pra `/hoje`, preservando query (?welcome=1) e hash (#item-<id>)
            // via RedirectWithToast. Bookmarks/notificações/deep-links
            // sobrevivem; o usuário aprende a URL nova.
            path: 'obra-hoje',
            element: (
              <RedirectWithToast
                to="../hoje"
                fromLabel="/obra-hoje"
                toLabel="Hoje"
                mergerKey="obra-hoje-to-hoje"
              />
            ),
          },
          {
            // Wave FFFFF · Project activity feed — full chronological history.
            path: 'atividade',
            element: (
              <Lazy>
                <ProjectActivityPage />
              </Lazy>
            ),
          },
          {
            path: 'visao-geral',
            element: (
              <Lazy>
                <VisaoGeralPage />
              </Lazy>
            ),
          },
          {
            // Wave 379BN · #87 fatia A · timeline shell. Aceita ?view=
            // feed (default) | gantt | calendar e renderiza o body
            // correspondente. Rotas /calendar e /cronograma-rup ficam
            // como redirect 308 abaixo pra preservar bookmarks.
            path: 'timeline',
            element: (
              <Lazy>
                <TimeCanvasPage />
              </Lazy>
            ),
          },
          {
            // Wave 379BN · redirect bookmark legado.
            // Wave codex-r8-legacy-redirects · 2026-05-19 · banner antes do hop.
            path: 'calendar',
            element: (
              <RedirectWithToast
                to="../timeline?view=calendar"
                fromLabel="/calendar"
                toLabel="Timeline · Calendário"
              />
            ),
          },
          // Wave merge-logs-rdos · 2026-05-21
          //
          // `/projects/:id/logs` and `/projects/:id/rdos` rendered the same
          // page (DailyLogsPage) under two URLs. nav-hierarchy already
          // points at `/rdos` (canonical), so the legacy `/logs` now
          // banner-redirects. PMs / foremen with bookmarks survive; the
          // banner teaches them the new URL.
          {
            path: 'logs',
            element: (
              <RedirectWithToast
                to="../rdos"
                fromLabel="/logs"
                toLabel="RDOs"
                mergerKey="logs"
              />
            ),
          },
          // Wave nav-fix · 2026-05-17 · /rdos era a entrada canônica de RDOs.
          // W123.10 · 2026-05-26 · /rdos passa a banner-redirect pra
          // /obra?tab=rdo (mesa unificada). Bookmarks/notificações continuam
          // funcionando; o banner ensina a nova URL.
          {
            path: 'rdos',
            element: (
              <RedirectWithToast
                to="../obra?tab=rdo"
                fromLabel="/rdos"
                toLabel="Obra · RDO"
                mergerKey="rdos-to-obra"
              />
            ),
          },
          // Wave merge-photos-fotos · 2026-05-21
          //
          // `/projects/:id/photos` (legacy outlet-based per-tab grid) and
          // `/projects/:id/fotos` (Wave NNNNN paginated gallery with
          // month/room/text filters) coexisted as separate nav entries.
          // JP picked `/fotos` as canonical — single page with two visions
          // (Álbuns vs Galeria filtrada). `/photos` now banner-redirects so
          // old links survive.
          {
            path: 'photos',
            element: (
              <RedirectWithToast
                to="../fotos"
                fromLabel="/photos"
                toLabel="Fotos · Galeria"
                mergerKey="photos"
              />
            ),
          },
          // Wave NNNNN · paginated photo gallery aggregated across RDOs.
          // W123.10 · 2026-05-26 · /fotos passa a banner-redirect pra
          // /obra?tab=fotos. A galeria continua viva via wrapper ObraFotos
          // (re-uso de ProjectPhotoGalleryPage dentro da mesa).
          {
            path: 'fotos',
            element: (
              <RedirectWithToast
                to="../obra?tab=fotos"
                fromLabel="/fotos"
                toLabel="Obra · Fotos"
                mergerKey="fotos-to-obra"
              />
            ),
          },
          { path: 'finance', element: <Lazy><FinancePage /></Lazy> },
          // Wave 379AU · /purchases foi substituído por /compras (Wave
          // 379U). Redireciona pra evitar bookmark órfão e duplicação
          // de Kanban. PurchasesPage fica no bundle só por enquanto —
          // será removido em wave de housekeeping.
          // 2026-05-11 (JP) · redirect agora preserva hash (#po-X) e
          // query string. Antes <Navigate> descartava o fragmento e os
          // links "Abrir PO" de notificações morriam na transição.
          { path: 'purchases', element: <PurchasesLegacyRedirect /> },
          // Wave 379U · página unificada Compras (Kanban Backlog → Cotações → POs).
          // W123.8 · 2026-05-26 · agora envolto em MesaTrabalhoComprasPage que
          // adiciona filtros de visão (A cotar / Cotando / Pedido / Recebido).
          {
            path: 'compras',
            element: (
              <Lazy>
                <MesaTrabalhoComprasPage />
              </Lazy>
            ),
          },
          // Wave MMMM · Compras Excel import wizard.
          {
            path: 'compras/excel-import',
            element: (
              <Lazy>
                <ComprasExcelImportPage />
              </Lazy>
            ),
          },
          // Wave NNNN · Cronograma Excel import wizard.
          {
            path: 'cronograma/excel-import',
            element: (
              <Lazy>
                <CronogramaExcelImportPage />
              </Lazy>
            ),
          },
          // Wave OOOO · Medições Excel import wizard.
          {
            path: 'medicoes/excel-import',
            element: (
              <Lazy>
                <MedicoesExcelImportPage />
              </Lazy>
            ),
          },
          // Wave DP2 · 5 new Excel importers.
          {
            path: 'verba/excel-import',
            element: (<Lazy><VerbaExcelImportPage /></Lazy>),
          },
          {
            path: 'estoque/excel-import',
            element: (<Lazy><EstoqueExcelImportPage /></Lazy>),
          },
          {
            path: 'rdos/excel-import',
            element: (<Lazy><DailyLogsExcelImportPage /></Lazy>),
          },
          {
            path: 'pranchas/excel-import',
            element: (<Lazy><PranchasExcelImportPage /></Lazy>),
          },
          {
            path: 'trena/excel-import',
            element: (<Lazy><TrenaExcelImportPage /></Lazy>),
          },
          // Trena · Wave (project-trena-overview) · 2026-05-15
          // Visão geral das estimativas do projeto com indicadores de confiança.
          // Substituiu TrenaListPage como a rota /projects/:id/trena.
          {
            path: 'trena',
            element: (
              <Lazy>
                <ProjectTrenaOverviewPage />
              </Lazy>
            ),
          },
          // W1F · 2026-05-12 · wizard for-dummies (default pra "Nova
          // estimativa"). Power users vão direto pra /trena/:eid via
          // detalhe.
          {
            path: 'trena/novo',
            element: (
              <Lazy>
                <TrenaWizardPage />
              </Lazy>
            ),
          },
          {
            path: 'trena/:eid',
            element: (
              <Lazy>
                <TrenaEstimatePage />
              </Lazy>
            ),
          },
          // Wave MMMMM · 2026-05-16 · /:left/compare/:right diff view.
          // Lazy-loaded via TrenaEstimateComparePage so the diff page does
          // NOT inflate the first-paint bundle (constraint: bundle).
          {
            path: 'trena/estimates/:left/compare/:right',
            element: (
              <Lazy>
                <TrenaEstimateComparePage />
              </Lazy>
            ),
          },
          // WAVE 34 · H3.4 · 2026-05-22 · Confidence multi-estimate compare.
          // /projects/:id/trena/compare?ids=A,B[,C]
          //
          // Distinct from the legacy `/trena/estimates/:left/compare/:right`
          // route above (which compares the composição-medida model). This
          // page renders the Confidence model diff (central/band/buckets +
          // sistemas + auto-analysis).
          {
            path: 'trena/compare',
            element: (
              <Lazy>
                <TrenaCompareEstimatesPage />
              </Lazy>
            ),
          },
          {
            // Trena · Done Page · 2026-05-15 · post-commit confirmation (bookmarkable)
            path: 'trena/estimates/:eid/done',
            element: (
              <Lazy>
                <TrenaDonePage />
              </Lazy>
            ),
          },
          {
            // FEATURE C1 · 2026-05-21 · quick-photo confirm
            // Foreman tira foto pela ObraHoje → backend cria estimate +
            // extrai medidas → essa página confirma as medidas extraídas.
            path: 'trena/medidas/:eid/confirmar',
            element: (
              <Lazy>
                <TrenaQuickPhotoConfirmPage />
              </Lazy>
            ),
          },
          {
            path: 'change-orders',
            element: (
              <Lazy>
                <ChangeOrdersPage />
              </Lazy>
            ),
          },
          {
            path: 'issues',
            element: (
              <Lazy>
                <IssuesPage />
              </Lazy>
            ),
          },
          {
            // W123.4 · 2026-05-26 · /orcamento absorvido por /plano.
            // O conteúdo real (BudgetPage) volta como aba quando W123.5
            // entrega a mesa Plano. Por enquanto redireciona pro
            // placeholder com query `?tab=orcamento` (a mesa real lê
            // esse query pra abrir na aba certa).
            //
            // BudgetPage continua importada (lazy) e pode voltar a ser
            // montada quando a mesa real for entregue. Bookmarks legados
            // chegam no placeholder com banner "em construção" + voltar.
            path: 'orcamento',
            element: <Navigate to="../plano?tab=orcamento" replace />,
          },
          {
            // W128.1 · 2026-05-26 · /projects/:id/budget era a rota antiga
            // do BudgetPage. W123.7 absorveu Orçamento como aba da mesa
            // /plano. PMs com bookmarks salvos no /budget caíam num
            // 404→/comando (sem aviso). Banner-redirect mantém o link
            // vivo e ensina a nova URL.
            path: 'budget',
            element: (
              <RedirectWithToast
                to="../plano?tab=orcamento"
                fromLabel="/budget"
                toLabel="Plano · Orçamento"
                mergerKey="budget-to-plano"
              />
            ),
          },
          {
            path: 'orcamento/excel-import',
            element: (
              <Lazy>
                <BudgetExcelImportPage />
              </Lazy>
            ),
          },
          {
            path: 'curva-abc',
            element: (
              <Lazy>
                <CurvaAbcPage />
              </Lazy>
            ),
          },
          {
            path: 'financeiro',
            element: (
              <Lazy>
                <FinancialDepthPage />
              </Lazy>
            ),
          },
          // Pranchas + RFIs unificadas (Wave TTTT).
          // W123.10 · 2026-05-26 · /pranchas passa a banner-redirect pra
          // /obra?tab=pranchas. ObraPranchas re-usa DrawingsRfiPage por
          // dentro — RFIs (dúvidas) ficam absorvidas na tab "Pranchas e
          // dúvidas" da Mesa Obra (JP-11).
          {
            path: 'pranchas',
            element: (
              <RedirectWithToast
                to="../obra?tab=pranchas"
                fromLabel="/pranchas"
                toLabel="Obra · Pranchas e dúvidas"
                mergerKey="pranchas-to-obra"
              />
            ),
          },
          {
            path: 'compliance',
            element: (
              <Lazy>
                <CompliancePage />
              </Lazy>
            ),
          },
          {
            // Wave 379BN · redirect bookmark legado pro shell unificado.
            // Wave codex-r8-legacy-redirects · 2026-05-19 · banner antes do hop.
            path: 'cronograma-rup',
            element: (
              <RedirectWithToast
                to="../timeline?view=gantt"
                fromLabel="/cronograma-rup"
                toLabel="Timeline · Gantt"
              />
            ),
          },
          // Wave relatorios-canonicos · 2026-05-21 · /relatorios é agora o
          // módulo dedicado dos 3 relatórios canônicos (boletim mensal,
          // fluxo de caixa, status cliente). Antes /reports redirecionava
          // pra /finance porque não havia página própria. Banner removido
          // (`mergerKey` ausente) porque a transição agora é direta:
          // /reports → /relatorios, mesmo módulo conceitual.
          {
            path: 'reports',
            element: (
              <RedirectWithToast
                to="../relatorios"
                fromLabel="/reports"
                toLabel="Relatórios"
              />
            ),
          },
          // Wave relatorios-canonicos · 2026-05-21 · página real dos 3
          // relatórios canônicos (project-scoped). Card por relatório,
          // gerar agora abre period picker inline, PDF abre em nova aba.
          {
            path: 'relatorios',
            element: (
              <Lazy>
                <RelatoriosPage />
              </Lazy>
            ),
          },
          // Wave nav-fix · 2026-05-17 · /cronograma is the nav-hierarchy
          // route for schedule. Redirects to timeline gantt view which is
          // the actual Gantt surface since Wave 379BN.
          // Wave codex-r8-legacy-redirects · 2026-05-19 · banner antes do hop.
          {
            path: 'cronograma',
            element: (
              <RedirectWithToast
                to="../timeline?view=gantt"
                fromLabel="/cronograma"
                toLabel="Timeline · Gantt"
              />
            ),
          },
          // Wave nav-fix · 2026-05-17 · /medicoes is the nav-hierarchy
          // route for progress billing. Redirects to /finance which hosts
          // the medições section (id="medicoes-section").
          // Wave codex-r8-legacy-redirects · 2026-05-19 · banner antes do hop.
          {
            path: 'medicoes',
            element: (
              <RedirectWithToast
                to="../finance#medicoes-section"
                fromLabel="/medicoes"
                toLabel="Financeiro · Medições"
                mergerKey="medicoes"
              />
            ),
          },
          // Wave nav-fix · 2026-05-17 · /rfi originalmente apontava pra
          // DrawingsRfiPage em /pranchas.
          // W123.10 · 2026-05-26 · agora vai direto pra /obra?tab=pranchas
          // (a mesa unificada da obra). DrawingsRfiPage continua sendo
          // o body — só mudamos o envelope.
          {
            path: 'rfi',
            element: (
              <RedirectWithToast
                to="../obra?tab=pranchas"
                fromLabel="/rfi"
                toLabel="Obra · Pranchas e dúvidas"
                mergerKey="rfi"
              />
            ),
          },
          {
            // W123.12 · 2026-05-26 · /entrega agora é o fluxo conduzido de
            // closeout (checklist forçado + assinatura termo + transição
            // status active→handoff→completed). PunchListPage continua
            // disponível em /entrega/punch-list pra walk-through legado.
            path: 'entrega',
            element: (
              <Lazy>
                <EntregaPage />
              </Lazy>
            ),
          },
          {
            path: 'entrega/punch-list',
            element: (
              <Lazy>
                <PunchListPage />
              </Lazy>
            ),
          },
          {
            path: 'mao-de-obra',
            element: (
              <Lazy>
                <LaborPage />
              </Lazy>
            ),
          },
          // W123.10 · 2026-05-26 · /rnc e /fvs passam a banner-redirect
          // pra /obra?tab=rnc | tab=fvs. Os bodies legados (RncPage,
          // FvsPage) continuam vivos dentro de ObraRnc / ObraFvs.
          {
            path: 'rnc',
            element: (
              <RedirectWithToast
                to="../obra?tab=rnc"
                fromLabel="/rnc"
                toLabel="Obra · RNC"
                mergerKey="rnc-to-obra"
              />
            ),
          },
          {
            path: 'fvs',
            element: (
              <RedirectWithToast
                to="../obra?tab=fvs"
                fromLabel="/fvs"
                toLabel="Obra · FVS"
                mergerKey="fvs-to-obra"
              />
            ),
          },
          {
            path: 'docs',
            element: (
              <Lazy>
                <DocsPage />
              </Lazy>
            ),
          },
          {
            path: 'members',
            element: (
              <Lazy>
                <MembersPage />
              </Lazy>
            ),
          },
          {
            // W146 · 2026-05-27 · QR code de presença (canteiro → WhatsApp).
            path: 'qr-presenca',
            element: (
              <Lazy>
                <ProjectQrCodePage />
              </Lazy>
            ),
          },
          {
            // Wave merge-activity-audit · 2026-05-21
            //
            // `/projects/:id/audit` and `/projects/:id/atividade` ran the same
            // backend (`api.listProjectEvents`) and answered the same PM
            // question ("o que mudou nessa obra?") in two different surfaces.
            // JP merged them: the canonical surface is now
            // `/projects/:id/atividade?view=audit` (tab inside
            // ProjectActivityPage), and this legacy path is a banner redirect
            // so notification/email bookmarks survive while the user learns
            // the new URL.
            path: 'audit',
            element: (
              <RedirectWithToast
                to="../atividade?view=audit"
                fromLabel="/audit"
                toLabel="Atividade · Trilha técnica"
              />
            ),
          },
          {
            path: 'verba',
            element: (
              <Lazy>
                <VerbaPage />
              </Lazy>
            ),
          },
          {
            path: 'demonstrativo',
            element: (
              <Lazy>
                <DemonstrativoPage />
              </Lazy>
            ),
          },
          {
            path: 'estoque',
            element: (
              <Lazy>
                <EstoquePage />
              </Lazy>
            ),
          },
          {
            path: 'checklists',
            element: (
              <Lazy>
                <ChecklistPage />
              </Lazy>
            ),
          },
          {
            // W138.3 · alias singular pra compat com links gerados no backend
            path: 'checklist',
            element: <Navigate to="../checklists" replace />,
          },
          {
            // Wave Bid-Hardening · 2026-05-11
            // Cotações consolidadas em /compras (Kanban Backlog→Cotações→PO).
            // Mantém URL antigo redirecionando pra não quebrar bookmarks.
            // Wave codex-r8-legacy-redirects · 2026-05-19 · banner antes do hop.
            // W123.4 · 2026-05-26 · destino agora carrega `?filter=cotando`
            // pra a ComprasPage (e a futura mesa Compras W123.7+) abrir
            // já filtrada na coluna "Cotando" — alinha com o resto dos
            // redirects PM (orcamento→plano?tab, recebimentos→compras?filter,
            // etc.). A query string passa pelo redirect (RedirectWithToast
            // só pré-pende o pathname).
            path: 'cotacoes',
            element: (
              <RedirectWithToast
                to="../compras?filter=cotando"
                fromLabel="/cotacoes"
                toLabel="Compras"
                mergerKey="cotacoes"
              />
            ),
          },
        ],
      },
      {
        path: '/owner',
        element: (
          <Lazy>
            <OwnerPortalPage />
          </Lazy>
        ),
      },
      {
        // FEATURE B2 · H3.2 · 2026-05-21 — promoted to OwnerProjectDetailPage
        // (Netflix-style visual). OwnerPortalPage continues to link here
        // via ProjectCard.href = `/owner/projects/${p.id}`.
        path: '/owner/projects/:id',
        element: (
          <Lazy>
            <OwnerProjectDetailPage />
          </Lazy>
        ),
      },
      {
        path: '/settings',
        element: (
          <Lazy>
            <SettingsPage />
          </Lazy>
        ),
      },
      // L1 · 2026-06-03 · alias PT-BR `/configuracoes` → `/settings`.
      // Antes caía no catch-all `*` → RootRedirect (dashboard). Agora
      // redireciona pra superfície canônica de Configurações. replace=true
      // não deixa entrada no history (back não volta pro alias).
      {
        path: '/configuracoes',
        element: <Navigate to="/settings" replace />,
      },
      // Wave 17: cross-project portfolio for admins / PMs / foremen.
      {
        path: '/carteira',
        element: (
          <Lazy>
            <CarteiraPage />
          </Lazy>
        ),
      },
      // Wave 19: central alerts hub.
      {
        path: '/alertas',
        element: (
          <Lazy>
            <AlertasPage />
          </Lazy>
        ),
      },
      // Wave 22: central approvals inbox.
      {
        path: '/aprovacoes',
        element: (
          <Lazy>
            <ApprovalsInboxPage />
          </Lazy>
        ),
      },
      // Wave 351-C — unified inbox surface (Hoje / Pendências / Decisões).
      // Tabs are URL-driven via ?tab= so links from Housy/Prumo can deep-link
      // into a specific bucket. Sibling to /aprovacoes, /alertas, /atividade
      // — replaces the need to bounce between them for the "what's on me?"
      // question.
      {
        path: '/inbox',
        element: (
          <Lazy>
            <InboxPage />
          </Lazy>
        ),
      },
      // FEATURE B1 · H2.2 · 2026-05-21 — `/hoje` now dispatches by role:
      //   - admin / project_manager → HojeCrossObraPage (cross-obra hub).
      //   - foreman / worker        → HojePage (legacy field-first queue).
      //
      // Why a wrapper instead of two URLs: HOME_BY_ROLE.project_manager is
      // `/hoje` (see nav-registry.ts → getBrandHomeForRole). Splitting the
      // route would force a second redirect hop on every PM session.
      // Wave 353 `/inbox?tab=hoje` compat still works for both paths.
      {
        path: '/hoje',
        element: <HojeRouter />,
      },
      // Wave 23: cross-project activity feed.
      {
        path: '/atividade',
        element: (
          <Lazy>
            <AtividadePage />
          </Lazy>
        ),
      },
      // Wave 98: top-level custom report builder.
      {
        path: '/relatorios',
        element: (
          <Lazy>
            <ReportsPage />
          </Lazy>
        ),
      },
      // Wave 86: anomaly inbox.
      {
        path: '/anomalias',
        element: (
          <Lazy>
            <AnomaliasPage />
          </Lazy>
        ),
      },
      // Wave UUUU: Qualidade Hub — top-level cross-project quality mode.
      // No projectId in path. Guard: useReadOnlyRedirect() inside page.
      {
        path: '/qualidade',
        element: (
          <MobileResilient loadingFallback={<QualityLoadingState surface="hub" />}>
            <QualidadeHubPage />
          </MobileResilient>
        ),
      },
      // Wave 32: Relatório Executivo — cross-project CEO dashboard.
      {
        path: '/executivo',
        element: (
          <Lazy>
            <ExecutivoPage />
          </Lazy>
        ),
      },
      // Wave 33: user approval queue (admin + PM).
      {
        path: '/aprovacoes-usuarios',
        element: (
          <Lazy>
            <ApproveUsersPage />
          </Lazy>
        ),
      },
      // Wave 43: Vitrine admin — list + per-showcase details page.
      // Wave 355 Run D · gateadas no nível de rota com
      // `<RequireAdminOrProjectManager>`. Backend já é admin/PM-only
      // em `apps/api/src/routes/wave40.ts`
      // (`showcaseAdminRoutes.use('*', requireRole('admin',
      // 'project_manager'))`); o guard de rota apenas espelha esse
      // contrato pra o frontend não montar página, fazer fetch e só
      // então a API responder 403.
      {
        path: '/vitrine',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <VitrineListPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      {
        path: '/vitrine/:id',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <VitrineDetailsPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      // Wave 35: help center.
      {
        path: '/ajuda',
        element: (
          <Lazy>
            <AjudaPage />
          </Lazy>
        ),
      },
      {
        path: '/ajuda/:slug',
        element: (
          <Lazy>
            <AjudaPage />
          </Lazy>
        ),
      },
      // Wave 89 polish · 301-style client redirect /suppliers → /fornecedores.
      // QA W84 notou que links externos legados ainda apontam pro slug em inglês.
      // SPA não emite 301 real, mas Navigate replace=true substitui no history
      // (back não volta pro /suppliers). O catch-all /suppliers/* abaixo
      // cobre /suppliers/:id, /suppliers/excel-import, etc., preservando o
      // sufixo via useLocation no componente RedirectSuppliers.
      {
        path: '/suppliers',
        element: <Navigate to="/fornecedores" replace />,
      },
      {
        path: '/suppliers/*',
        element: <RedirectSuppliersSubpath />,
      },
      // Wave 10: top-level tenant-scoped surfaces.
      {
        path: '/fornecedores',
        element: (
          <Lazy>
            <SuppliersPage />
          </Lazy>
        ),
      },
      // Wave 21: per-supplier scorecard.
      {
        path: '/fornecedores/:id',
        element: (
          <Lazy>
            <SupplierDetailsPage />
          </Lazy>
        ),
      },
      // Wave NNNN · Fornecedores Excel import wizard (PM/admin-only, tenant-scoped).
      {
        path: '/fornecedores/excel-import',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <FornecedoresExcelImportPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      // Wave 1.5 · 2026-05-10 · catálogo de itens (SKUs canônicos)
      {
        path: '/catalogo',
        element: (
          <Lazy>
            <CatalogoPage />
          </Lazy>
        ),
      },
      {
        path: '/notificacoes',
        element: (
          <Lazy>
            <NotificationsPage />
          </Lazy>
        ),
      },
      {
        // Wave ggggg · 2026-05-16 · Admin home page — tile rollup dashboard.
        // Replaces the old AdminPage shell at this path. AdminPage is kept
        // in the bundle for any existing deep-links that may still reference it.
        path: '/admin',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminHomePage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave comando · 2026-05-17 · Command Center — single-pane-of-glass.
        // Primary entry point for admin: onde tudo começa.
        path: '/comando',
        element: (
          <RequireAdmin>
            <Lazy>
              <ComandoPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave comando · 2026-05-17 · Configurações consolidadas.
        path: '/admin/configuracoes',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminConfiguracoesPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave comando · 2026-05-17 · Históricos consolidados.
        path: '/admin/historicos',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminHistoricosPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      // Wave 379AI · Command & Control — Kanban consolidado das ~41
      // páginas admin.
      {
        path: '/admin/command-control',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <AdminCommandControlPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      {
        path: '/admin/usuarios',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <AdminUsersPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      // W1I · 2026-05-12 · catálogo de insumos · admin/PM
      {
        path: '/admin/insumos',
        element: (
          <RequireAdminOrProjectManager>
            <Lazy>
              <AdminInsumosPage />
            </Lazy>
          </RequireAdminOrProjectManager>
        ),
      },
      // Wave 393A · 2026-05-15 · Admin override audit history. Admin-only.
      {
        path: '/admin/trena/overrides',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminTrenaOverridesPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      // Wave QQQ · 2026-05-16 · Admin follow-ups telemetry. Admin-only.
      {
        path: '/admin/follow-ups',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminFollowUpsPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      // Wave 392 · 2026-05-15 · Admin Trena R2 storage stats. Admin-only.
      {
        path: '/admin/trena/storage',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminTrenaStoragePage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      // Wave 393B · 2026-05-15 · Admin Trena compositions compat audit. Admin-only.
      {
        path: '/admin/trena/compositions',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminTrenaCompositionsPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        path: '/admin/deploys',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminDeploysPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 347 — Housy Brain command center. Admin-only registry
        // of every AI/automation capability + live runtime + toggles.
        path: '/admin/brain',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminBrainPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        path: '/admin/brain/goal-plans',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminGoalPlansPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 366 R3 — Agent Orchestrator admin page. Toggle
        // auto-orchestrate per plan + inspect recent runs + per-step
        // results + R2 attempts ledger.
        path: '/admin/brain/orchestrator',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminOrchestratorPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 332 — admin-only saúde dos módulos.
        path: '/admin/saude',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminSaudePage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        path: '/admin/autonomia',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminAutonomiaPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        path: '/admin/conectores',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminConectoresPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 369 R4 — proposer A/B win/loss scoreboard. Read-only.
        // Promotion gate is R5.
        path: '/admin/proposer-ab',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminProposerAbPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 285e R2 — Housy Unknowns Curation UI.
        path: '/admin/housy/unknowns',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminHousyUnknownsPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 379CU — Housy debug QA harness (force-state isolated).
        // Not linked from nav; URL-only surface for admin polish QA.
        path: '/admin/housy-debug',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminHousyDebugPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        // Wave 380 — Product Analytics dashboard.
        path: '/admin/product-analytics',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminProductAnalyticsPage />
            </Lazy>
          </RequireAdmin>
        ),
      },

        {
          path: '/admin/observability/decision-graph',
          element: (
            <RequireAdmin>
              <AdminDecisionGraphPage />
            </RequireAdmin>
          ),
        },
        {
          // Wave 384 — Monthly Close admin page
          path: '/admin/monthly-close',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminMonthlyClosePage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 390 — Financial Risk Autopilot admin page
          path: '/admin/financial-risk',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminFinancialRiskPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 385 → Wave merge-autonomy-dupes · 2026-05-21
          // Wave admin-redirect-instant · 2026-05-22 — JP smoke report:
          // o `RedirectWithToast` (1.5s) somado ao cold-start do useQuery
          // da página destino fazia o redirect parecer "app travado por
          // ~10s". Para essas 3 rotas admin (data-self-heal, procurement-
          // autopilot, rule-autopilot) o ganho do banner pedagógico não
          // vale o custo de fluidez — admin entra raramente via bookmark,
          // não precisa "aprender a URL nova". Declarativo `<Navigate>`
          // resolve no instante; a página destino monta direto e mostra
          // seu próprio skeleton enquanto carrega.
          //
          // O padrão `RedirectWithToast` continua válido para rotas que
          // PMs/usuários comuns usam dia-a-dia (cotacoes→compras, etc.),
          // onde aprender o novo path tem valor recorrente.
          //
          // The 5 autonomy admin pages were originally mounted at
          // `/admin/<feature>` and later remounted under
          // `/admin/autonomy/<feature>` without retiring the old paths.
          // Two routes pointing at the same component is a concierge
          // failure (sidenav, search, links could land on either). JP
          // picked the `/admin/autonomy/*` family as canonical.
          path: '/admin/data-self-heal',
          element: (
            <RequireAdmin>
              <Navigate to="/admin/autonomy/data-self-heal" replace />
            </RequireAdmin>
          ),
        },
        {
          // Wave 386 → Wave merge-autonomy-dupes · 2026-05-21
          path: '/admin/workflow-self-heal',
          element: (
            <RequireAdmin>
              <RedirectWithToast
                to="/admin/autonomy/workflow-self-heal"
                fromLabel="/admin/workflow-self-heal"
                toLabel="Autonomia · Workflow self-heal"
              />
            </RequireAdmin>
          ),
        },
        {
          // Wave 387 → Wave merge-autonomy-dupes · 2026-05-21
          // Wave admin-redirect-instant · 2026-05-22 — ver comentário em
          // `/admin/data-self-heal` acima. `<Navigate>` instantâneo
          // (sem 1.5s do toast pedagógico) elimina o "app travado".
          path: '/admin/procurement-autopilot',
          element: (
            <RequireAdmin>
              <Navigate to="/admin/autonomy/procurement-autopilot" replace />
            </RequireAdmin>
          ),
        },
        {
          // LR-W4 · 2026-05-15 · Supplier/broker dashboard.
          path: '/broker',
          element: (
            <RequireSupplier>
              <Lazy>
                <BrokerDashboardPage />
              </Lazy>
            </RequireSupplier>
          ),
        },
        {
          // LR-W4 · 2026-05-15 · Supplier auction history.
          path: '/broker/history',
          element: (
            <RequireSupplier>
              <Lazy>
                <BrokerAuctionHistoryPage />
              </Lazy>
            </RequireSupplier>
          ),
        },
        {
          // LR-W4 · 2026-05-15 · Supplier round detail view.
          path: '/broker/rounds/:roundId',
          element: (
            <RequireSupplier>
              <Lazy>
                <BrokerRoundDetailPage />
              </Lazy>
            </RequireSupplier>
          ),
        },
        {
          // LR-W3 · 2026-05-14 · Leilões reversos — lista. Admin OU PM.
          path: '/admin/auctions',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <AdminAuctionsPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // LR-W3 · 2026-05-14 · Leilões reversos — detalhe + live bids. Admin OU PM.
          path: '/admin/auctions/:roundId',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <AdminAuctionDetailPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // LR-W2 #51 · 2026-05-11 · Procurement Cockpit (Modality Recommender
          // shadow lab). Admin OU PM (guard via RequireAdminOrProjectManager).
          // Página interativa pra preencher cenário e ver recomendação.
          // Cada cálculo grava em model_inference_log.
          path: '/admin/procurement-cockpit',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <ProcurementCockpitPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // LR-W2 #51 · 2026-05-11 · Contratos guarda-chuva (framework contracts).
          // List + promote-from-award + drawdown UI. Admin OU PM.
          path: '/admin/procurement-frameworks',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <ProcurementFrameworksPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // LR-W2 #56 · 2026-05-11 · Painel de compras (KPIs cross-obras +
          // savings na janela). Admin OU PM.
          path: '/admin/procurement-dashboard',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <ProcurementDashboardPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // Wave 388 → Wave merge-autonomy-dupes · 2026-05-21
          path: '/admin/resource-optimizer',
          element: (
            <RequireAdmin>
              <RedirectWithToast
                to="/admin/autonomy/resource-optimizer"
                fromLabel="/admin/resource-optimizer"
                toLabel="Autonomia · Resource optimizer"
              />
            </RequireAdmin>
          ),
        },
        {
          // Wave 389 → Wave merge-autonomy-dupes · 2026-05-21
          // Wave admin-redirect-instant · 2026-05-22 — ver comentário em
          // `/admin/data-self-heal` acima. `<Navigate>` instantâneo
          // (sem 1.5s do toast pedagógico) elimina o "app travado".
          path: '/admin/rule-autopilot',
          element: (
            <RequireAdmin>
              <Navigate to="/admin/autonomy/rule-autopilot" replace />
            </RequireAdmin>
          ),
        },
        {
          // Wave 401 — Autonomy Incident Commander admin page
          path: '/admin/autonomy/incidents',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminIncidentsPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 381 — PM Loop admin page
          path: '/admin/autonomy/pm-loop',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminPMLoopPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 391 — Quality Autopilot admin page
          path: '/admin/autonomy/quality-autopilot',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminQualityAutopilotPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 392 — Claims Detection admin page
          path: '/admin/autonomy/claims-detection',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminClaimsDetectionPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 393 — Owner Trust Plus admin page
          path: '/admin/autonomy/owner-trust-plus',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminOwnerTrustPlusPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 394 — Agent Governor admin page
          path: '/admin/autonomy/agent-governor',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminAgentGovernorPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 395 — Self-Tuning admin page
          path: '/admin/autonomy/self-tuning',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminSelfTuningPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 396 — Daily Brief admin page
          path: '/admin/autonomy/daily-brief',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminDailyBriefPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 403 — SLA admin page
          path: '/admin/autonomy/sla',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminSLAPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 383 — Radar Critic admin page
          path: '/admin/autonomy/radar/critic',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminRadarCriticPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 368 — Chaos admin page
          path: '/admin/autonomy/chaos',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminChaosPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 41 · 2026-05-23 — Meta WhatsApp template watcher.
          path: '/admin/meta-templates',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminMetaTemplatesPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 47 · 2026-05-23 — Generative Engineering Labs (AI sketch demo).
          path: '/admin/labs/generative',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminLabsGenerativePage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 67 F2.4 · 2026-05-23 — Generative Lab history.
          path: '/admin/labs/generative/historico',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminLabsGenerativeHistoryPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave SYSTEM-TASKS · 2026-05-20 — Tarefas do sistema (scheduler interno).
          path: '/admin/system-tasks',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminSystemTasksPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 397 — Exceptions Only admin page
          path: '/admin/autonomy/exceptions-only',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminExceptionsOnlyPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 385 — Data Self-Heal with new path
          path: '/admin/autonomy/data-self-heal',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminDataSelfHealPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 386 — Workflow Self-Heal with new path
          path: '/admin/autonomy/workflow-self-heal',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminWorkflowSelfHealPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 387 — Procurement Autopilot with new path
          path: '/admin/autonomy/procurement-autopilot',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminProcurementAutopilotPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 388 — Resource Optimizer with new path
          path: '/admin/autonomy/resource-optimizer',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminResourceOptimizerPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 389 — Rule Autopilot with new path
          path: '/admin/autonomy/rule-autopilot',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminRuleAutopilotPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 400 — Audit Pack admin page
          path: '/admin/audit-pack',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminAuditPackPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 398 — Memory Graph admin page
          path: '/admin/observability/memory-graph',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminMemoryGraphPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 405 — Benchmarking admin page
          path: '/admin/benchmarking',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminBenchmarkingPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 404 — SOP admin page
          path: '/admin/sop',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminSOPPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 402 — Policy admin page
          path: '/admin/policy',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminPolicyPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 399 — Simulation admin page
          path: '/admin/simulation',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminSimulationPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 410 — Tenant Switcher admin page
          path: '/admin/tenants',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminTenantsPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 411 — Global Search results page (non-admin)
          path: '/search',
          element: (
            <Lazy>
              <SearchResultsPage />
            </Lazy>
          ),
        },
        {
          // Wave 412 — Bulk Operations admin page
          path: '/admin/bulk-ops',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminBulkOpsPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave 413 — Audit Log Viewer admin page
          path: '/admin/audit-log',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminAuditLogPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Error tracking · 2026-05-20 — CF-native client error firehose.
          path: '/admin/observability',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminObservabilityPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // 2026-05-21 — Trena funnel telemetry (Stage 1 → 3 abandonment).
          path: '/admin/analytics/trena-funnel',
          element: (
            <RequireAdminOrProjectManager>
              <Lazy>
                <AdminTrenaFunnelPage />
              </Lazy>
            </RequireAdminOrProjectManager>
          ),
        },
        {
          // Wave 414 — Notification Preferences settings page (non-admin)
          path: '/settings/notifications',
          element: (
            <Lazy>
              <SettingsNotificationsPage />
            </Lazy>
          ),
        },
        {
          // Wave pm-uom · 2026-05-17 — Unidades de medida customizadas (PM/admin).
          path: '/settings/unidades',
          element: (
            <Lazy>
              <SettingsUomPage />
            </Lazy>
          ),
        },
        {
          // Wave 415 — Backup Self-Service admin page
          path: '/admin/backups',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminBackupsPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        {
          // Wave M — Trena Sanity Ranges admin page
          path: '/admin/trena/sanity-ranges',
          element: (
            <RequireAdmin>
              <Lazy>
                <AdminTrenaSanityRangesPage />
              </Lazy>
            </RequireAdmin>
          ),
        },
        ], // ← end MaintenanceRedirect children
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
  ], // ← end root errorElement wrapper children
  },
]);
